/**
 * MislabeledProductsDialog
 *
 * Lists catalog products whose name / subcategory hints at one
 * `main_category` but are filed under another. One-click "Fix" applies
 * the suggested category. Pure read-only scan unless the admin clicks
 * Fix — no auto-write magic.
 */
import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { api } from '../../lib/store';
import { toast } from 'sonner';
import { Loader2, Tags, CheckCircle2, Wand2 } from 'lucide-react';

export default function MislabeledProductsDialog({ open, onOpenChange }) {
  const [scanning, setScanning] = useState(false);
  const [scanned, setScanned] = useState(0);
  const [candidates, setCandidates] = useState(null);
  const [fixingId, setFixingId] = useState(null);

  const runScan = async () => {
    setScanning(true);
    try {
      const res = await api.findMislabeledProducts();
      setScanned(res.data.total_scanned);
      setCandidates(res.data.candidates || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Scan failed');
    } finally {
      setScanning(false);
    }
  };

  const handleFix = async (c) => {
    setFixingId(c.id);
    try {
      await api.fixMislabeledProduct(c.id, c.suggested_category);
      toast.success(`${c.brand_name} → ${c.suggested_category}`);
      setCandidates((prev) => prev.filter((x) => x.id !== c.id));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Fix failed');
    } finally {
      setFixingId(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[92vh] flex flex-col" data-testid="mislabeled-products-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Tags className="w-5 h-5 text-[#556B2F]" /> Find Mislabeled Products
          </DialogTitle>
          <p className="text-xs text-[#5C6150] mt-1">
            Scans your catalog for products whose name/subcategory suggests a different category. Fixes are one-click and reversible (edit the product again to revert).
          </p>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto pr-1 space-y-3">
          {candidates === null && (
            <div className="border border-dashed border-[#E2E6D9] rounded-md p-6 text-center text-sm text-[#5C6150]">
              Click <strong>Run Scan</strong> to find products whose category doesn&apos;t match their name.
            </div>
          )}
          {scanning && (
            <div className="flex items-center justify-center gap-2 text-sm text-[#5C6150] py-6">
              <Loader2 className="w-4 h-4 animate-spin" /> Scanning {scanned > 0 ? `${scanned}` : 'all'} products…
            </div>
          )}
          {candidates !== null && !scanning && (
            <>
              <div className="text-xs text-[#5C6150]">
                Scanned <strong className="text-[#1A1C16] font-mono-data">{scanned}</strong> products · Found <strong className={candidates.length > 0 ? 'text-amber-700' : 'text-[#1B7531]'}>{candidates.length}</strong> candidates
              </div>
              {candidates.length === 0 ? (
                <div className="bg-[#25D366]/10 border border-[#25D366]/30 rounded-md p-4 text-sm text-[#1B7531] flex items-center gap-2" data-testid="mislabeled-allclean">
                  <CheckCircle2 className="w-4 h-4" /> Your catalog is clean — no mislabeled products found.
                </div>
              ) : (
                <div className="border border-[#E2E6D9] rounded-md overflow-x-auto bg-white">
                  <table className="w-full text-xs">
                    <thead className="bg-[#F2F4EF] text-[#5C6150]">
                      <tr>
                        <th className="text-left py-2 px-3">Product</th>
                        <th className="text-left py-2 px-3">Current</th>
                        <th className="text-left py-2 px-3">Suggested</th>
                        <th className="text-right py-2 px-3">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {candidates.map((c) => (
                        <tr key={c.id} className="border-t border-[#E2E6D9]" data-testid={`mislabeled-row-${c.id}`}>
                          <td className="py-2 px-3">
                            <p className="font-semibold text-[#1A1C16]">{c.brand_name}</p>
                            <p className="text-[10px] text-[#8F9485]">{c.subcategory} {c.thickness && `· ${c.thickness}`}</p>
                          </td>
                          <td className="py-2 px-3">
                            <Badge variant="outline" className="text-[10px] border-red-200 text-red-700 bg-red-50">{c.current_category}</Badge>
                          </td>
                          <td className="py-2 px-3">
                            <Badge variant="outline" className="text-[10px] border-[#25D366]/30 text-[#1B7531] bg-[#25D366]/10">{c.suggested_category}</Badge>
                          </td>
                          <td className="py-2 px-3 text-right">
                            <Button
                              size="sm"
                              className="h-7 text-[10px] bg-[#556B2F] hover:bg-[#465926] text-white"
                              onClick={() => handleFix(c)}
                              disabled={fixingId === c.id}
                              data-testid={`mislabeled-fix-${c.id}`}
                            >
                              {fixingId === c.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <><Wand2 className="w-3 h-3 mr-1" /> Fix</>}
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </div>

        <DialogFooter className="flex-shrink-0 pt-3 border-t border-[#E2E6D9] gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={scanning} data-testid="mislabeled-close-btn">
            Close
          </Button>
          <Button
            onClick={runScan}
            disabled={scanning}
            className="bg-[#556B2F] hover:bg-[#465926] text-white"
            data-testid="mislabeled-scan-btn"
          >
            {scanning ? <><Loader2 className="w-3 h-3 mr-2 animate-spin" /> Scanning…</> : (candidates === null ? 'Run Scan' : 'Re-scan')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
